import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

// Types for the Web App Launch Handler API (window.launchQueue), which is not
// yet part of the standard DOM lib. Paired with the manifest's
// `launch_handler: { client_mode: "focus-existing" }`.
// See https://developer.mozilla.org/en-US/docs/Web/API/LaunchParams
declare global {
  interface LaunchParams {
    readonly targetURL?: string;
    readonly files: readonly FileSystemHandle[];
  }

  interface LaunchQueue {
    setConsumer(consumer: (params: LaunchParams) => void): void;
  }

  interface Window {
    readonly launchQueue?: LaunchQueue;
  }
}

/**
 * Consumes the Web App Launch Handler queue so that, when the PWA is launched
 * into an already-open window (manifest `launch_handler.client_mode:
 * "focus-existing"`), we perform a soft client-side navigation to the launch
 * target instead of letting the window sit on whatever route it was already
 * showing.
 *
 * `focus-existing` (vs `navigate-existing`) brings the window to focus WITHOUT
 * the browser hard-navigating it to the launch URL, so a plain app-resume —
 * where the launch target equals the current route — no-ops via the
 * redundant-push guard below instead of triggering a full document reload.
 *
 * This is what makes a link clicked in a sent message reuse the existing
 * Snowball window rather than accumulating new tabs. Renders nothing.
 */
const LaunchQueueHandler: React.FC = () => {
  const history = useHistory();

  useEffect(() => {
    if (!('launchQueue' in window) || !window.launchQueue) {
      return;
    }

    window.launchQueue.setConsumer(launchParams => {
      if (!launchParams.targetURL) {
        return;
      }

      try {
        const target = new URL(launchParams.targetURL);
        // Only navigate within our own origin — never follow a launch target
        // that resolves off-origin.
        if (target.origin !== window.location.origin) {
          return;
        }

        const destination = target.pathname + target.search + target.hash;
        // Avoid a redundant push if we're already there (e.g. the OS launched
        // us straight onto the target route).
        if (
          destination !==
          window.location.pathname +
            window.location.search +
            window.location.hash
        ) {
          history.push(destination);
        }
      } catch {
        // Ignore malformed launch targets.
      }
    });
  }, [history]);

  return null;
};

export default LaunchQueueHandler;
